/**
 * Step 2 -- Purchase & Customer Details
 *
 * Two-column layout: purchase details (left), customer info + address (right).
 * All simple fields render through MetadataField for centralised control.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Receipt, UserCircle, Building2, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { SearchInput } from "@/shared/components/ui/search-input";
import { Label } from "@/shared/components/ui/label";
import {
  useConsumerLookup,
  usePartnershipSearch,
  useUploadFile,
} from "@/admin/hooks/use-products";
import { useOrg } from "@/admin/hooks/use-org";
import { usePartnerPickerScope } from "@/admin/hooks/use-partner-picker-scope";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { useEntityTypeByKey } from "@/admin/hooks/use-entities";
import {
  useDataCaptureConfig,
  isCollapsed,
  isVisible,
  isRequired,
} from "@/admin/hooks/use-data-capture-config";
import type { WizardState } from "@/admin/pages/registrations/new-registration-page";
import type { RegistrationFormMetadata } from "@/admin/types/products";
import type { DataCaptureConfig } from "@/admin/hooks/use-registration-settings";
import {
  RegistrationDisclosureSection,
  RegistrationPanel,
  RegistrationStepActions,
  RegistrationStepGrid,
} from "./layout";
import { MailingAddressCapture } from "./mailing-address-capture";
import { normaliseAddressObject } from "./address-utils";
import {
  ADDRESS_PENDING_SEARCH_MESSAGE,
  FILE_FIELD_TYPES,
  MetadataField,
  normaliseTemplateAttribute,
} from "@/shared/lib/forms";
import type { FormFieldMeta } from "@/shared/lib/forms";
import {
  isValidPhone,
  PHONE_VALIDATION_MESSAGE,
  splitStoredValue,
} from "@/shared/lib/forms/phone";
import {
  coreContactConfigMap,
  contactBackendValues,
  resolveContactField,
} from "./contact-field-config";
import {
  useRequiredFieldErrors,
  type RequiredFieldSpec,
} from "./wizard-validation";
import { RegistrantEmailTick } from "./registrant-email-tick";
import {
  startFollowsPurchase,
  warrantyStartDefault,
  warrantyStartSource,
} from "./warranty-start-source";

/* ── Props ───────────────────────────────────────────────── */

interface StepProps {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  next: () => void;
  back?: () => void;
  continueDisabled?: boolean;
  continueHint?: string | null;
  dataCaptureConfig?: DataCaptureConfig;
  earlyCaptureFields?: string[];
  metadata?: RegistrationFormMetadata | null;
  /**
   * Extra panel(s) to render alongside this step's own panels in the
   * shared `RegistrationStepGrid`.
   */
  extraPanels?: React.ReactNode;
}

/* ── Component ───────────────────────────────────────────── */

export function StepPurchaseCustomer({
  state,
  update,
  next,
  back,
  // The legacy `continueDisabled` prop is intentionally not
  // destructured. Continue is always enabled; validation is handled
  // via the wizard validation context.
  continueHint,
  dataCaptureConfig,
  earlyCaptureFields = [],
  metadata,
  extraPanels,
}: StepProps) {
  const { t } = useTranslation();
  const { dcc, toggleField, isFieldShown, fieldLabel } =
    useDataCaptureConfig(dataCaptureConfig);

  /* --- Formatting --- */
  const { currencySymbol } = useFormatting();
  const uploadFile = useUploadFile();

  const rawTransactionAttrs = metadata?.transaction_template?.attributes;
  const transactionAttrs = useMemo(() => {
    if (!rawTransactionAttrs) return null;
    return [...rawTransactionAttrs].sort((a, b) => a.order - b.order);
  }, [rawTransactionAttrs]);
  const hasTransactionTemplate = !!transactionAttrs;
  /* --- Dealer --- */
  const { orgUuid, tenantSettings } = useOrg();
  // Who may pick, and from which set: the tenant's whole book for an internal
  // user, a partner's own locations for a partner that has any.
  const dealerScope = usePartnerPickerScope();
  const [dealerSearch, setDealerSearch] = useState("");
  // A partner picks among its own locations, so the search has to reach
  // sub-locations; an internal user searches the parent-level book as before.
  // `activeOnly` narrows in SQL. Filtering suspended rows out on the client
  // instead let them consume the single page the server returns, so an active
  // dealer sorting past it simply never appeared.
  const dealerSearchOptions = useMemo(
    () =>
      dealerScope.ownLocationsOnly
        ? { includeLocations: true, activeOnly: true }
        : { activeOnly: true },
    [dealerScope.ownLocationsOnly],
  );
  const { data: dealerResults, isFetching: dealerSearching } =
    usePartnershipSearch(orgUuid, dealerSearch, dealerSearchOptions);
  const requirePartner = dealerScope.required;
  // Show the Dealer picker only when the org actually has a downstream
  // partnership (dealer) network; with none, the picker is always empty.
  const showDealer =
    dealerScope.visible && (tenantSettings?.has_downstream_partnerships ?? true);

  /* --- Email lookup --- */
  // Seeded from the stored email so a revisit knows whose it is again.
  const [emailForLookup, setEmailForLookup] = useState(() => {
    const stored = state.consumerEmail.trim().toLowerCase();
    return stored.includes("@") ? stored : "";
  });
  const [prefilled, setPrefilled] = useState(false);
  const { data: lookup } = useConsumerLookup(
    emailForLookup,
    state.partnershipUuid,
  );
  // The registrant's own email is never a customer to pre-fill: ask whose it
  // is. A lookup for an earlier email says nothing about the one typed now.
  const isRegistrantEmail =
    lookup?.isRegistrantEmail === true &&
    emailForLookup === state.consumerEmail.trim().toLowerCase();
  const existingConsumer = lookup?.customer ?? null;

  /* --- Address ---
     Lookup provider sourced from the consumer entity `address` attribute
     (e.g. google_places); undefined falls back to org-routed lookup (OC). */
  const { data: consumerEntity } = useEntityTypeByKey("consumer");
  const addressLookupProvider =
    (consumerEntity?.attributes?.find(
      (a) => a.code_name === "address" && a.field_type === "ADDRESS",
    )?.configuration_data?.lookup_provider as string | undefined) ?? undefined;

  /* --- Purchase field definitions --- */

  const warrantyStartField = useMemo(
    (): FormFieldMeta => ({
      code_name: "warrantyStartDate",
      display_name: "Warranty Start Date",
      field_type: "DATE",
      required: false,
      description: "Defaults to today if not set",
      order: 1,
    }),
    [],
  );

  const purchasePriceField = useMemo(
    (): FormFieldMeta => ({
      code_name: "purchasePrice",
      display_name: "Purchase Price",
      field_type: "MONEY",
      required: false,
      hint: "0.00",
      order: 2,
    }),
    [],
  );

  const salespersonField = useMemo(
    (): FormFieldMeta => ({
      code_name: "salesPerson",
      display_name: "Salesperson",
      field_type: "TEXT",
      required: false,
      hint: "Optional",
      order: 3,
    }),
    [],
  );

  const salesRefField = useMemo(
    (): FormFieldMeta => ({
      code_name: "salesRefNumber",
      display_name: "Sales Reference",
      field_type: "TEXT",
      required: false,
      hint: "Optional",
      order: 4,
    }),
    [],
  );

  const normalisedTransactionAttrs = useMemo(
    () => (transactionAttrs ?? []).map(normaliseTemplateAttribute),
    [transactionAttrs],
  );

  const purchaseDateTemplateField = useMemo(
    () =>
      normalisedTransactionAttrs.find(
        (field) =>
          field.code_name === "purchase_date" &&
          !field.configuration_data?.hidden,
      ),
    [normalisedTransactionAttrs],
  );

  const warrantyStartDateTemplateField = useMemo(
    () =>
      normalisedTransactionAttrs.find(
        (field) => field.code_name === "warranty_start_date",
      ),
    [normalisedTransactionAttrs],
  );

  const warrantyStartDefaultSource = useMemo(
    () => warrantyStartSource(normalisedTransactionAttrs),
    [normalisedTransactionAttrs],
  );

  const warrantyStartDefaultValue = useMemo(
    () =>
      warrantyStartDefault(
        warrantyStartDefaultSource,
        state.purchaseDate,
        state.earlyAttributes,
      ),
    [warrantyStartDefaultSource, state.purchaseDate, state.earlyAttributes],
  );

  const renderedTransactionFields = useMemo(
    () =>
      normalisedTransactionAttrs.filter(
        (field) =>
          field.code_name !== "purchase_date" &&
          field.code_name !== "warranty_start_date" &&
          !field.configuration_data?.hidden,
      ),
    [normalisedTransactionAttrs],
  );

  /* --- Customer field definitions (from DataCaptureConfig) ---
     Base required/visible come from the tenant DataCaptureConfig; any
     `required_when` / `visible_when` authored on the backend consumer
     attributes (delivered via `metadata.consumer_fields.configuration_data`)
     is layered on top by `resolveContactField`, so e.g. the WPS-118
     phone-or-email rule is honoured with zero bespoke cross-field logic.
     With no config, the resolver returns the base flags unchanged. */

  const contactConfigMap = useMemo(
    () => coreContactConfigMap(metadata?.consumer_fields),
    [metadata?.consumer_fields],
  );
  const contactValues = useMemo(
    () =>
      contactBackendValues({
        consumerEmail: state.consumerEmail,
        consumerFirstName: state.consumerFirstName,
        consumerLastName: state.consumerLastName,
        consumerPhone: state.consumerPhone,
      }),
    [
      state.consumerEmail,
      state.consumerFirstName,
      state.consumerLastName,
      state.consumerPhone,
    ],
  );

  const customerFields = useMemo((): FormFieldMeta[] => {
    const fields: FormFieldMeta[] = [];
    const emailFlags = resolveContactField(
      "consumerEmail",
      contactConfigMap,
      contactValues,
      !dcc || isRequired(dcc.consumer_email),
      isVisible(dcc?.consumer_email),
    );
    if (emailFlags.visible) {
      fields.push({
        code_name: "consumerEmail",
        display_name: fieldLabel("consumer_email", "Email"),
        field_type: "EMAIL",
        required: emailFlags.required,
        hint: "customer@example.com",
        order: 0,
      });
    }
    const nameBaseVisible = isVisible(dcc?.consumer_name);
    const nameBaseRequired = isRequired(dcc?.consumer_name);
    const firstFlags = resolveContactField(
      "consumerFirstName",
      contactConfigMap,
      contactValues,
      nameBaseRequired,
      nameBaseVisible,
    );
    const lastFlags = resolveContactField(
      "consumerLastName",
      contactConfigMap,
      contactValues,
      nameBaseRequired,
      nameBaseVisible,
    );
    if (firstFlags.visible) {
      fields.push({
        code_name: "consumerFirstName",
        display_name: "First Name",
        field_type: "TEXT",
        required: firstFlags.required,
        hint: "First name",
        order: 1,
      });
    }
    if (lastFlags.visible) {
      fields.push({
        code_name: "consumerLastName",
        display_name: "Last Name",
        field_type: "TEXT",
        required: lastFlags.required,
        hint: "Last name",
        order: 2,
      });
    }
    const phoneFlags = resolveContactField(
      "consumerPhone",
      contactConfigMap,
      contactValues,
      isRequired(dcc?.consumer_phone),
      isVisible(dcc?.consumer_phone),
    );
    if (phoneFlags.visible) {
      fields.push({
        code_name: "consumerPhone",
        display_name: fieldLabel("consumer_phone", "Phone Number"),
        field_type: "PHONE",
        required: phoneFlags.required,
        hint: "Phone number",
        order: 3,
      });
    }
    return fields;
  }, [dcc, fieldLabel, contactConfigMap, contactValues]);

  /* --- Value accessors mapped to WizardState --- */

  const fieldValueMap: Record<string, unknown> = useMemo(
    () => ({
      purchaseDate: state.purchaseDate,
      warrantyStartDate: state.warrantyStartDate || warrantyStartDefaultValue,
      purchasePrice: state.purchasePrice,
      salesPerson: state.salesPerson,
      salesRefNumber: state.salesRefNumber,
      consumerEmail: state.consumerEmail,
      consumerFirstName: state.consumerFirstName,
      consumerLastName: state.consumerLastName,
      consumerPhone: state.consumerPhone,
    }),
    [
      state.purchaseDate,
      state.warrantyStartDate,
      warrantyStartDefaultValue,
      state.purchasePrice,
      state.salesPerson,
      state.salesRefNumber,
      state.consumerEmail,
      state.consumerFirstName,
      state.consumerLastName,
      state.consumerPhone,
    ],
  );

  const fieldUpdateMap: Record<string, (v: unknown) => void> = useMemo(
    () => ({
      purchaseDate: (v) => update({ purchaseDate: (v as string) ?? "" }),
      warrantyStartDate: (v) =>
        update({ warrantyStartDate: (v as string) ?? "" }),
      purchasePrice: (v) =>
        update({ purchasePrice: v != null ? String(v) : "" }),
      salesPerson: (v) => update({ salesPerson: (v as string) ?? "" }),
      salesRefNumber: (v) => update({ salesRefNumber: (v as string) ?? "" }),
      consumerEmail: (v) =>
        update({ consumerEmail: (v as string) ?? "", consumerEmailIsOwners: null }),
      consumerFirstName: (v) =>
        update({ consumerFirstName: (v as string) ?? "" }),
      consumerLastName: (v) =>
        update({ consumerLastName: (v as string) ?? "" }),
      consumerPhone: (v) => update({ consumerPhone: (v as string) ?? "" }),
    }),
    [update],
  );

  const getTransactionValue = useCallback(
    (codeName: string): unknown => state.transactionData?.[codeName] ?? "",
    [state.transactionData],
  );

  const getRenderedTransactionValue = useCallback(
    (codeName: string): unknown => {
      switch (codeName) {
        case "purchase_date":
          return state.purchaseDate;
        case "warranty_start_date":
          return state.warrantyStartDate || warrantyStartDefaultValue;
        case "purchase_price": {
          // MONEY fields round-trip as { amount, currency }; reconstruct that
          // shape from the string `state.purchasePrice` for the CurrencyInput.
          const amount = state.purchasePrice
            ? Number.parseFloat(state.purchasePrice)
            : undefined;
          return {
            amount: Number.isFinite(amount) ? amount : undefined,
            currency: state.priceCurrency || undefined,
          };
        }
        case "sales_person":
          return state.salesPerson;
        case "sales_reference":
          return state.salesRefNumber;
        default:
          return getTransactionValue(codeName);
      }
    },
    [
      getTransactionValue,
      state.purchaseDate,
      state.warrantyStartDate,
      warrantyStartDefaultValue,
      state.purchasePrice,
      state.priceCurrency,
      state.salesPerson,
      state.salesRefNumber,
    ],
  );

  function setRenderedTransactionValue(codeName: string, value: unknown) {
    const nextTransactionData = {
      ...state.transactionData,
      [codeName]: value,
    };
    const patch: Partial<WizardState> = {
      transactionData: nextTransactionData,
    };

    switch (codeName) {
      case "purchase_date":
        patch.purchaseDate = (value as string) ?? "";
        // A start the user chose gives way when the purchase date passes it.
        if (
          warrantyStartDefaultSource === "purchase_date" &&
          patch.purchaseDate &&
          state.warrantyStartDate &&
          startFollowsPurchase(
            state.warrantyStartDate,
            state.purchaseDate,
            patch.purchaseDate,
          )
        ) {
          patch.warrantyStartDate = "";
        }
        break;
      case "warranty_start_date":
        patch.warrantyStartDate = (value as string) ?? "";
        break;
      case "purchase_price": {
        // CurrencyInput emits { amount, currency }; persist the numeric
        // amount as the string `purchasePrice` expects, not String(object).
        const money = value as
          { amount?: number; currency?: string } | undefined;
        patch.purchasePrice = money?.amount != null ? String(money.amount) : "";
        patch.priceCurrency = money?.currency ?? state.priceCurrency;
        break;
      }
      case "sales_person":
        patch.salesPerson = (value as string) ?? "";
        break;
      case "sales_reference":
        patch.salesRefNumber = (value as string) ?? "";
        break;
    }

    update(patch);
  }

  useEffect(() => {
    if (!purchaseDateTemplateField && !warrantyStartDateTemplateField) return;

    const nextTransactionData = { ...state.transactionData };
    let changed = false;

    if (
      purchaseDateTemplateField &&
      state.purchaseDate &&
      nextTransactionData.purchase_date !== state.purchaseDate
    ) {
      nextTransactionData.purchase_date = state.purchaseDate;
      changed = true;
    }

    const effectiveWarrantyStartDate =
      state.warrantyStartDate || warrantyStartDefaultValue;
    if (
      warrantyStartDateTemplateField &&
      effectiveWarrantyStartDate &&
      nextTransactionData.warranty_start_date !== effectiveWarrantyStartDate
    ) {
      nextTransactionData.warranty_start_date = effectiveWarrantyStartDate;
      changed = true;
    }

    if (changed) {
      update({ transactionData: nextTransactionData });
    }
  }, [
    purchaseDateTemplateField,
    warrantyStartDateTemplateField,
    warrantyStartDefaultValue,
    state.purchaseDate,
    state.warrantyStartDate,
    state.transactionData,
    update,
  ]);

  async function handleFileUpload(file: File, field: FormFieldMeta) {
    const configuredUseCase =
      field.file_use_case ?? field.configuration_data?.file_use_case;
    const useCase =
      typeof configuredUseCase === "string"
        ? configuredUseCase
        : "PROOF_OF_PURCHASE";
    return uploadFile.mutateAsync({ file, use_case: useCase });
  }

  /* --- Handlers --- */

  const handleEmailBlur = useCallback(() => {
    const trimmed = state.consumerEmail.trim().toLowerCase();
    if (trimmed && trimmed.includes("@")) {
      setEmailForLookup(trimmed);
      setPrefilled(false);
    }
  }, [state.consumerEmail]);

  function handlePrefill() {
    if (!existingConsumer) return;
    const patch: Partial<WizardState> = {
      consumerFirstName: existingConsumer.first_name || state.consumerFirstName,
      consumerLastName: existingConsumer.last_name || state.consumerLastName,
      consumerPhone: existingConsumer.phone_number || state.consumerPhone,
    };
    // Pre-fill the address from the consumer's stored EAV attributes. Address
    // is the custom consumer attribute the wizard renders today; it maps to the
    // canonical `state.address` slot regardless of its code_name, so matching on
    // field_type keeps this tenant-agnostic and resilient to future custom
    // address attributes.
    const addressAttr = Array.isArray(existingConsumer.attribute_values)
      ? existingConsumer.attribute_values.find(
          (attr) => attr.field_type === "ADDRESS" && attr.value != null,
        )
      : undefined;
    if (addressAttr) {
      patch.address = normaliseAddressObject(addressAttr.value);
    }
    update(patch);
    setPrefilled(true);
  }

  /* --- Per-field required validation. Continue is always enabled;
        empty required fields surface inline red borders + hints. */
  const dealerRequired = requirePartner && showDealer;
  const addressCapturedEarly = earlyCaptureFields.includes("consumer_address");
  const addressVisible =
    !addressCapturedEarly && isVisible(dcc?.consumer_address);
  const addressRequired = addressVisible && isRequired(dcc?.consumer_address);
  const addressFilled =
    !!state.address &&
    Object.values(state.address).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
  // Unresolved text typed into the address lookup (no suggestion selected, no
  // manual entry). Blocks Continue even when the field is optional so the text
  // is never silently discarded - matching the consumer app's validation.
  const [addressPendingTerm, setAddressPendingTerm] = useState("");
  const addressHasPending =
    addressVisible && addressPendingTerm.trim().length > 0;
  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    const specs: RequiredFieldSpec[] = [];
    if (hasTransactionTemplate) {
      for (const f of normalisedTransactionAttrs.filter(
        (a) => a.required && !a.configuration_data?.hidden,
      )) {
        const val = getRenderedTransactionValue(f.code_name);
        const isFilled =
          typeof val === "string"
            ? val.trim().length > 0
            : val != null && val !== "";
        specs.push({
          key: f.code_name,
          label: f.display_name,
          isFilled,
        });
      }
    }
    for (const f of customerFields.filter((cf) => cf.required)) {
      const val = fieldValueMap[f.code_name];
      const trimmed = typeof val === "string" ? val.trim() : "";
      let isFilled: boolean;
      if (typeof val === "string") {
        isFilled = trimmed.length > 0;
        if (isFilled && f.field_type === "EMAIL")
          isFilled = trimmed.includes("@");
        if (isFilled && f.field_type === "PHONE")
          isFilled = isValidPhone(trimmed);
      } else {
        isFilled = !!val;
      }
      let message = `${f.display_name} is required`;
      if (
        f.field_type === "EMAIL" &&
        trimmed.length > 0 &&
        !trimmed.includes("@")
      ) {
        message = "Enter a valid email address";
      } else if (
        f.field_type === "PHONE" &&
        splitStoredValue(trimmed).base.length > 0 &&
        !isValidPhone(trimmed)
      ) {
        message = PHONE_VALIDATION_MESSAGE;
      }
      specs.push({
        key: f.code_name,
        label: f.display_name,
        isFilled,
        message,
      });
    }
    if (dealerRequired) {
      specs.push({
        key: "partnership",
        label: "Dealer",
        isFilled: !!state.partnershipUuid,
        message: "Dealer is required",
      });
    }
    if (addressRequired || addressHasPending) {
      specs.push({
        key: "consumer_address",
        label: "Address",
        // Unresolved lookup text is never a valid address, even when the field
        // is optional; a required-but-empty address keeps its default message.
        isFilled: addressHasPending ? false : addressFilled,
        message: addressHasPending ? ADDRESS_PENDING_SEARCH_MESSAGE : undefined,
      });
    }
    return specs;
  }, [
    hasTransactionTemplate,
    normalisedTransactionAttrs,
    getRenderedTransactionValue,
    customerFields,
    fieldValueMap,
    dealerRequired,
    state.partnershipUuid,
    addressRequired,
    addressFilled,
    addressHasPending,
  ]);
  const errors = useRequiredFieldErrors("purchase_customer", requiredSpecs);
  const dealerError = errors["partnership"];
  const addressError = errors["consumer_address"];

  /* --- Render --- */

  return (
    <div>
      {/* Two-column grid - autoComplete="off" on wrapper form to suppress password managers */}
      <RegistrationStepGrid
        className="gap-10"
        data-lpignore="true"
        data-form-type="other"
      >
        {/* ── LEFT: Purchase Details ─────────────────────── */}
        <RegistrationPanel
          title="Purchase Details"
          icon={<Receipt size={14} className="text-[var(--ink-3)]" />}
        >
          {/* Purchase Date (only shown when template field is visible) */}
          {purchaseDateTemplateField && (
            <MetadataField
              field={purchaseDateTemplateField}
              value={state.purchaseDate}
              onChange={(v) => setRenderedTransactionValue("purchase_date", v)}
              labelVariant="compact"
              error={errors["purchase_date"]}
            />
          )}

          {/* Warranty Start Date: driven by the transaction template.
                When a template is linked, the field shows only if the
                template defines warranty_start_date; a template that omits
                it (e.g. a purchase-date-led flow) intentionally drops it.
                The hardcoded fallback applies only when no template exists. */}
          {(warrantyStartDateTemplateField || !hasTransactionTemplate) && (
            <MetadataField
              field={warrantyStartDateTemplateField ?? warrantyStartField}
              value={fieldValueMap.warrantyStartDate}
              minDate={
                warrantyStartDefaultSource === "purchase_date"
                  ? state.purchaseDate || undefined
                  : undefined
              }
              onChange={(v) => {
                if (warrantyStartDateTemplateField) {
                  setRenderedTransactionValue("warranty_start_date", v);
                } else {
                  fieldUpdateMap.warrantyStartDate(v);
                }
              }}
              labelVariant="compact"
              error={errors["warranty_start_date"]}
            />
          )}

          {hasTransactionTemplate ? (
            renderedTransactionFields.map((field) => {
              const isFileField = FILE_FIELD_TYPES.has(
                field.field_type.toUpperCase(),
              );
              return (
                <MetadataField
                  key={field.code_name}
                  field={field}
                  value={getRenderedTransactionValue(field.code_name)}
                  onChange={(v) =>
                    setRenderedTransactionValue(field.code_name, v)
                  }
                  labelVariant="compact"
                  error={errors[field.code_name]}
                  onFileUpload={isFileField ? handleFileUpload : undefined}
                  uploading={isFileField && uploadFile.isPending}
                />
              );
            })
          ) : (
            <>
              {/* Purchase Price - currency prefix needs wrapper */}
              <div>
                <Label className="gap-0 mb-1.5 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {purchasePriceField.display_name}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[var(--ink-3)] select-none pointer-events-none">
                    {currencySymbol}
                  </span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={state.purchasePrice}
                    onChange={(e) => update({ purchasePrice: e.target.value })}
                    placeholder="0.00"
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="h-9 w-full rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] pl-7 pr-3 text-[13px] text-[var(--ink)] outline-none transition-all duration-150 ease"
                  />
                </div>
              </div>

              {/* Salesperson */}
              <MetadataField
                field={salespersonField}
                value={fieldValueMap.salesPerson}
                onChange={fieldUpdateMap.salesPerson}
                labelVariant="compact"
              />

              {/* Sales Reference */}
              <MetadataField
                field={salesRefField}
                value={fieldValueMap.salesRefNumber}
                onChange={fieldUpdateMap.salesRefNumber}
                labelVariant="compact"
              />
            </>
          )}

          {/* Dealer - complex search widget, keeps custom UI.
                Hidden when the org has no downstream partnership (dealer)
                network, since the picker would always be empty. */}
          {showDealer && (
            <div>
              <Label className="gap-0 mb-1.5 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Dealer
                {requirePartner && (
                  <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
                    *
                  </span>
                )}
              </Label>
              {state.partnershipUuid ? (
                <div className="flex h-9 w-full items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 text-base text-[var(--ink)]">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-[var(--green)]" />
                    <span className="text-[13px] text-[var(--ink)]">
                      {state.partnershipName}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() =>
                      update({ partnershipUuid: null, partnershipName: null })
                    }
                    className="h-6 w-6 cursor-pointer rounded-md text-[var(--ink-3)]"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div>
                  <SearchInput
                    value={dealerSearch}
                    onChange={setDealerSearch}
                    placeholder="Search dealerships..."
                    autoComplete="off"
                    iconSize={12}
                    className={`h-9 text-[13px] ${dealerError ? "border-destructive" : ""}`}
                  />
                  {dealerError ? (
                    <p className="mt-1 text-xs text-[var(--status-error)]">
                      {dealerError}
                    </p>
                  ) : null}
                  {/* Say what a blank selection resolves to, so a partner
                      browsing its locations knows leaving this alone is a
                      valid choice. */}
                  {!dealerError && dealerScope.defaultsToName && (
                    <p className="mt-1 text-[10px] text-[var(--ink-3)]">
                      {t(
                        "registration.partnerDefaultsTo",
                        "Defaults to {{name}}",
                        { name: dealerScope.defaultsToName },
                      )}
                    </p>
                  )}
                  {dealerSearch.length >= 2 &&
                    (dealerResults ?? []).length > 0 && (
                      <div className="mt-2 flex max-h-[180px] flex-col gap-0.5 overflow-y-auto border-t-[0.5px] border-t-[var(--rule)] pt-2">
                        {(dealerResults ?? []).map((p) => (
                            <Button
                              key={p.uuid}
                              variant="ghost"
                              onClick={() => {
                                update({
                                  partnershipUuid: p.uuid,
                                  partnershipName:
                                    p.downstream_organisation.name,
                                });
                                setDealerSearch("");
                              }}
                              className="flex h-auto items-center justify-start gap-2.5 rounded-[4px] px-2 py-2 text-left font-light cursor-pointer"
                            >
                              <Building2 className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                              <span className="text-[12px] text-[var(--ink)]">
                                {p.downstream_organisation.name}
                              </span>
                            </Button>
                          ))}
                      </div>
                    )}
                  {/* Only once the fetch has settled - a term mid-flight has
                      no results yet, and claiming the dealer does not exist is
                      exactly the report we are fixing. */}
                  {dealerSearch.length >= 2 &&
                    !dealerSearching &&
                    (dealerResults ?? []).length === 0 && (
                      <p className="mt-2 text-center text-[11px] text-[var(--ink-3)]">
                        No dealerships found
                      </p>
                    )}
                </div>
              )}
            </div>
          )}
        </RegistrationPanel>

        {/* ── RIGHT: Customer Details ────────────────────── */}
        <RegistrationPanel
          title={t("registrations.steps.customerDetails")}
          icon={<UserCircle size={14} className="text-[var(--ink-3)]" />}
        >
          {/* Customer fields from DataCaptureConfig - rendered via MetadataField */}
          {customerFields.map((field) => {
            const fieldMode = (() => {
              if (field.code_name === "consumerEmail")
                return dcc?.consumer_email;
              if (
                field.code_name === "consumerFirstName" ||
                field.code_name === "consumerLastName"
              ) {
                return dcc?.consumer_name;
              }
              if (field.code_name === "consumerPhone")
                return dcc?.consumer_phone;
              return undefined;
            })();

            const collapseKey = (() => {
              if (field.code_name === "consumerEmail") return "email";
              if (
                field.code_name === "consumerFirstName" ||
                field.code_name === "consumerLastName"
              )
                return "name";
              if (field.code_name === "consumerPhone") return "phone";
              return "";
            })();
            const isCollapsible = collapseKey !== "" && isCollapsed(fieldMode);
            const isShown = isCollapsible
              ? isFieldShown(collapseKey, fieldMode)
              : true;

            // Skip the second name field - we render both names together
            if (field.code_name === "consumerLastName") return null;

            // Name fields: render side by side
            if (field.code_name === "consumerFirstName") {
              const lastNameField = customerFields.find(
                (f) => f.code_name === "consumerLastName",
              );
              const content = (
                <div className="grid grid-cols-2 gap-3">
                  <MetadataField
                    field={field}
                    value={fieldValueMap[field.code_name]}
                    onChange={fieldUpdateMap[field.code_name]}
                    labelVariant="compact"
                    error={errors[field.code_name]}
                  />
                  {lastNameField && (
                    <MetadataField
                      field={lastNameField}
                      value={fieldValueMap[lastNameField.code_name]}
                      onChange={fieldUpdateMap[lastNameField.code_name]}
                      labelVariant="compact"
                      error={errors[lastNameField.code_name]}
                    />
                  )}
                </div>
              );

              if (isCollapsible) {
                return (
                  <RegistrationDisclosureSection
                    key="name-row"
                    label={`Add ${fieldLabel("consumer_name", "Name")}`}
                    open={isShown}
                    onToggle={() => toggleField("name")}
                  >
                    {content}
                  </RegistrationDisclosureSection>
                );
              }

              return <div key="name-row">{content}</div>;
            }

            // Email: needs onBlur for consumer lookup + prefill hint
            if (field.code_name === "consumerEmail") {
              const content = (
                <div key={field.code_name}>
                  <MetadataField
                    field={field}
                    value={fieldValueMap[field.code_name]}
                    onChange={fieldUpdateMap[field.code_name]}
                    onBlur={handleEmailBlur}
                    labelVariant="compact"
                    error={errors[field.code_name]}
                  />
                  <RegistrantEmailTick
                    email={state.consumerEmail}
                    isRegistrantEmail={isRegistrantEmail}
                    emailIsOwners={state.consumerEmailIsOwners}
                    onChange={(isOwners) =>
                      update({ consumerEmailIsOwners: isOwners })
                    }
                  />
                  {existingConsumer &&
                    !prefilled &&
                    state.consumerEmailIsOwners !== false && (
                    <p className="mt-1.5 text-[11px] text-[var(--green)]">
                      Existing customer&nbsp;-&nbsp;
                      <Button
                        variant="link"
                        type="button"
                        onClick={handlePrefill}
                        className="underline cursor-pointer bg-transparent border-none p-0 text-[11px] font-light text-[var(--green)] hover:opacity-80 transition-all duration-150 ease"
                      >
                        pre-fill?
                      </Button>
                    </p>
                  )}
                </div>
              );

              if (isCollapsible) {
                return (
                  <RegistrationDisclosureSection
                    key={field.code_name}
                    label={`Add ${field.display_name}`}
                    open={isShown}
                    onToggle={() => toggleField(collapseKey)}
                  >
                    {content}
                  </RegistrationDisclosureSection>
                );
              }

              return content;
            }

            // Default: render the MetadataField directly
            if (isCollapsible) {
              return (
                <RegistrationDisclosureSection
                  key={field.code_name}
                  label={`Add ${field.display_name}`}
                  open={isShown}
                  onToggle={() => toggleField(collapseKey)}
                >
                  <MetadataField
                    field={field}
                    value={fieldValueMap[field.code_name]}
                    onChange={fieldUpdateMap[field.code_name]}
                    labelVariant="compact"
                    error={errors[field.code_name]}
                  />
                </RegistrationDisclosureSection>
              );
            }

            return (
              <MetadataField
                key={field.code_name}
                field={field}
                value={fieldValueMap[field.code_name]}
                onChange={fieldUpdateMap[field.code_name]}
                labelVariant="compact"
                error={errors[field.code_name]}
              />
            );
          })}

          {/* Address - the single canonical AddressCapture component, on the
                same surface as the other customer fields (no divider). */}
          {!addressCapturedEarly &&
            isVisible(dcc?.consumer_address) &&
            (isCollapsed(dcc?.consumer_address) ? (
              <RegistrationDisclosureSection
                label={`Add ${fieldLabel("consumer_address", "Address")}`}
                open={isFieldShown("address", dcc?.consumer_address)}
                onToggle={() => toggleField("address")}
              >
                <div>
                  <Label className="gap-0 mb-2 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    {fieldLabel("consumer_address", "Address")}
                    {isRequired(dcc?.consumer_address) && (
                      <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
                        *
                      </span>
                    )}
                  </Label>
                  <MailingAddressCapture
                    address={state.address}
                    onChange={(next) => update({ address: next })}
                    error={addressError}
                    lookupProvider={addressLookupProvider}
                    onPendingChange={setAddressPendingTerm}
                  />
                </div>
              </RegistrationDisclosureSection>
            ) : isFieldShown("address", dcc?.consumer_address) ? (
              <div>
                <Label className="gap-0 mb-2 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {fieldLabel("consumer_address", "Address")}
                  {isRequired(dcc?.consumer_address) && (
                    <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
                      *
                    </span>
                  )}
                </Label>
                <MailingAddressCapture
                  address={state.address}
                  onChange={(next) => update({ address: next })}
                  error={addressError}
                  lookupProvider={addressLookupProvider}
                />
              </div>
            ) : null)}
        </RegistrationPanel>

        {extraPanels}
      </RegistrationStepGrid>
      {/* ── Navigation ───────────────────────────────────── */}
      <RegistrationStepActions
        onBack={back}
        onContinue={next}
        continueHint={continueHint}
        withDivider
      />
    </div>
  );
}
